import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { getDeliverability, getDeliveryAvailableAddressLine, validateFullAddress } from '@api/interfaces/postalApi';
import useCustomer from '@hooks/useCustomer';
import { AddressData } from '@occ/api-client';

interface UseAddressValidationProps {
  savedCustomerAddress: AddressData | null;
  setInputErrorMessage: (msg: string) => void;
}

export const useAddressValidation = ({ savedCustomerAddress, setInputErrorMessage }: UseAddressValidationProps) => {
  const { customer } = useCustomer();
  const t = useTranslations('deliverypicker');

  // Step 4: Check valid delivery has delivery available
  const checkDeliveryAvailability = useCallback(
    async (formattedAddress: string) => {
      try {
        const response = await getDeliveryAvailableAddressLine(formattedAddress);
        if (!response?.data?.isDeliveryAvailable) {
          throw new Error(t('error.noDeliveryAvailable'));
        }
        return response;
      } catch {
        throw new Error(t('error.noDeliveryAvailable'));
      }
    },
    [t]
  );

  // Step 3: Address is validated, validate deliverability
  const validateDeliverability = useCallback(
    async (formattedAddress: string, postalCode?: string) => {
      if (!postalCode || !customer) {
        throw new Error(t('error.noDeliveryAvailable'));
      }

      const response = await getDeliverability(encodeURIComponent(postalCode), customer.isB2BCustomer);
      const data = response.data;

      if (!data.deliverable) {
        throw new Error(t('error.noDeliveryAvailable'));
      }

      if (data.isDivided) {
        await checkDeliveryAvailability(formattedAddress);
      }

      return data.deliveryStoreId;
    },
    [checkDeliveryAvailability, customer, t]
  );

  // Step 2 - ONLY IF MANUALLY TYPED
  const validateAddress = useCallback(
    async (formattedAddress: string) => {
      try {
        const res = await validateFullAddress(formattedAddress);
        if (res.data.response.is_valid) {
          const match = res.data.response.match;
          return {
            ...match,
            postalCode: match?.postalcode,
            streetNumber: match?.street_number,
          };
        }
        throw new Error(t('error.invalidAddress'));
      } catch {
        throw new Error(t('error.invalidAddress'));
      }
    },
    [t]
  );

  // Step 1. Start validation before submitting address
  const validate = useCallback(
    async (address: string, useSavedAddress: boolean) => {
      try {
        let addressParams: AddressParamsType;
        const addressLine2 = savedCustomerAddress?.line2 || '';

        if (useSavedAddress && savedCustomerAddress) {
          // Using saved address (suggestion for you)
          addressParams = {
            addressLine: savedCustomerAddress.line1,
            addressLine2,
            postalCode: savedCustomerAddress.postalCode || '',
            locality: savedCustomerAddress.town,
            companyName: customer?.companyAlternativeName || savedCustomerAddress?.companyName,
          };
        } else {
          // Using manually typed address
          const validated = await validateAddress(address);
          addressParams = {
            addressLine: `${validated.street || ''} ${validated.streetNumber || ''}${validated.letter || ''}`,
            postalCode: validated.postalCode,
            locality: validated.locality,
            addressLine2,
          };
        }

        const deliveryStoreId = await validateDeliverability(address, addressParams.postalCode);
        return { addressParams, deliveryStoreId };
      } catch (err) {
        if (err instanceof Error && setInputErrorMessage) {
          setInputErrorMessage(err.message);
        }
        throw new Error('Validation failed');
      }
    },
    [customer, savedCustomerAddress, validateAddress, validateDeliverability, setInputErrorMessage]
  );

  return { validate };
};
